import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../utils/config';

const REQUEST_TIMEOUT_MS = 20000;

const CustomerModal = ({ isOpen, onClose, customer, onSave, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    basic_information: {
      title: '',
      customer_name: '',
      entity_type: '',
      customer_id: '',
      mobile_number: '',
      email_address: '',
      customer_gstin: '',
      gst_registered_name: '',
      gst_filing_status: '',
      business_name: '',
      display_name: '',
      phone_number: '',
      fax: ''
    },
    billing_address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      branch_name: ''
    },
    shipping_address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      branch_name: ''
    },
    banking_details: {
      account_number: '',
      account_name: '',
      bank_name: '',
      ifsc_code: '',
      account_type: '',
      branch_name: ''
    },
    tax_details: {
      pan: '',
      tan: '',
      coa: '',
      tds_slab_rate: '',
      currency: '',
      apply_reverse_charge_by_default: false,
      export_or_sez_developer: false,
      msme_number: '',
      customer_code: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (isOpen) {
      if (customer && mode === 'edit') {
        // Populate form with customer data for edit mode
        setFormData({
          basic_information: {
            title: customer?.customer_data?.basic_information?.title || '',
            customer_name: customer?.customer_data?.basic_information?.customer_name || '',
            entity_type: customer?.customer_data?.basic_information?.entity_type || '',
            customer_id: customer?.customer_data?.basic_information?.customer_id || '',
            mobile_number: customer?.customer_data?.basic_information?.mobile_number || '',
            email_address: customer?.customer_data?.basic_information?.email_address || '',
            customer_gstin: customer?.customer_data?.basic_information?.customer_gstin || '',
            gst_registered_name: customer?.customer_data?.basic_information?.gst_registered_name || '',
            gst_filing_status: customer?.customer_data?.basic_information?.gst_filing_status || '',
            business_name: customer?.customer_data?.basic_information?.business_name || '',
            display_name: customer?.customer_data?.basic_information?.display_name || '',
            phone_number: customer?.customer_data?.basic_information?.phone_number || '',
            fax: customer?.customer_data?.basic_information?.fax || ''
          },
          billing_address: {
            address_line_1: customer?.customer_data?.billing_address?.address_line_1 || '',
            address_line_2: customer?.customer_data?.billing_address?.address_line_2 || '',
            city: customer?.customer_data?.billing_address?.city || '',
            state: customer?.customer_data?.billing_address?.state || '',
            postal_code: customer?.customer_data?.billing_address?.postal_code || '',
            country: customer?.customer_data?.billing_address?.country || '',
            branch_name: customer?.customer_data?.billing_address?.branch_name || ''
          },
          shipping_address: {
            address_line_1: customer?.customer_data?.shipping_address?.address_line_1 || '',
            address_line_2: customer?.customer_data?.shipping_address?.address_line_2 || '',
            city: customer?.customer_data?.shipping_address?.city || '',
            state: customer?.customer_data?.shipping_address?.state || '',
            postal_code: customer?.customer_data?.shipping_address?.postal_code || '',
            country: customer?.customer_data?.shipping_address?.country || '',
            branch_name: customer?.customer_data?.shipping_address?.branch_name || ''
          },
          banking_details: {
            account_number: customer?.customer_data?.banking_details?.account_number || '',
            account_name: customer?.customer_data?.banking_details?.account_name || '',
            bank_name: customer?.customer_data?.banking_details?.bank_name || '',
            ifsc_code: customer?.customer_data?.banking_details?.ifsc_code || '',
            account_type: customer?.customer_data?.banking_details?.account_type || '',
            branch_name: customer?.customer_data?.banking_details?.branch_name || ''
          },
          tax_details: {
            pan: customer?.customer_data?.tax_details?.pan || '',
            tan: customer?.customer_data?.tax_details?.tan || '',
            coa: customer?.customer_data?.tax_details?.coa || '',
            tds_slab_rate: customer?.customer_data?.tax_details?.tds_slab_rate || '',
            currency: customer?.customer_data?.tax_details?.currency || '',
            apply_reverse_charge_by_default: customer?.customer_data?.tax_details?.apply_reverse_charge_by_default || false,
            export_or_sez_developer: customer?.customer_data?.tax_details?.export_or_sez_developer || false,
            msme_number: customer?.customer_data?.tax_details?.msme_number || '',
            customer_code: customer?.customer_data?.tax_details?.customer_code || ''
          }
        });
      } else {
        // Reset form for add mode
        setFormData({
          basic_information: {
            title: '',
            customer_name: '',
            entity_type: '',
            customer_id: '',
            mobile_number: '',
            email_address: '',
            customer_gstin: '',
            gst_registered_name: '',
            gst_filing_status: '',
            business_name: '',
            display_name: '',
            phone_number: '',
            fax: ''
          },
          billing_address: {
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            branch_name: ''
          },
          shipping_address: {
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            branch_name: ''
          },
          banking_details: {
            account_number: '',
            account_name: '',
            bank_name: '',
            ifsc_code: '',
            account_type: '',
            branch_name: ''
          },
          tax_details: {
            pan: '',
            tan: '',
            coa: '',
            tds_slab_rate: '',
            currency: '',
            apply_reverse_charge_by_default: false,
            export_or_sez_developer: false,
            msme_number: '',
            customer_code: ''
          }
        });
      }
      setErrors({});
      setSuccessMessage('');
      setErrorMessage('');
      setActiveSection('basic');
    }
  }, [customer, mode, isOpen]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors?.[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate Basic Information (required fields)
    if (!formData?.basic_information?.customer_name?.trim()) {
      newErrors['basic_information.customer_name'] = 'Customer name is required';
    }

    if (!formData?.basic_information?.email_address?.trim()) {
      newErrors['basic_information.email_address'] = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.basic_information?.email_address)) {
      newErrors['basic_information.email_address'] = 'Please enter a valid email address';
    }

    if (!formData?.basic_information?.mobile_number?.trim()) {
      newErrors['basic_information.mobile_number'] = 'Mobile number is required';
    }

    if (!formData?.basic_information?.entity_type?.trim()) {
      newErrors['basic_information.entity_type'] = 'Entity type is required';
    }

    if (!formData?.basic_information?.customer_id?.trim()) {
      newErrors['basic_information.customer_id'] = 'Customer ID is required';
    }

    // Validate Billing Address (required fields)
    if (!formData?.billing_address?.address_line_1?.trim()) {
      newErrors['billing_address.address_line_1'] = 'Address line 1 is required';
    }

    if (!formData?.billing_address?.city?.trim()) {
      newErrors['billing_address.city'] = 'City is required';
    }

    if (!formData?.billing_address?.state?.trim()) {
      newErrors['billing_address.state'] = 'State is required';
    }

    if (!formData?.billing_address?.postal_code?.trim()) {
      newErrors['billing_address.postal_code'] = 'Postal code is required';
    }

    if (!formData?.billing_address?.country?.trim()) {
      newErrors['billing_address.country'] = 'Country is required';
    }

    // Validate Shipping Address (required fields)
    if (!formData?.shipping_address?.address_line_1?.trim()) {
      newErrors['shipping_address.address_line_1'] = 'Address line 1 is required';
    }

    if (!formData?.shipping_address?.city?.trim()) {
      newErrors['shipping_address.city'] = 'City is required';
    }

    if (!formData?.shipping_address?.state?.trim()) {
      newErrors['shipping_address.state'] = 'State is required';
    }

    if (!formData?.shipping_address?.postal_code?.trim()) {
      newErrors['shipping_address.postal_code'] = 'Postal code is required';
    }

    if (!formData?.shipping_address?.country?.trim()) {
      newErrors['shipping_address.country'] = 'Country is required';
    }

    // Validate Banking Details (required fields)
    if (!formData?.banking_details?.account_number?.trim()) {
      newErrors['banking_details.account_number'] = 'Account number is required';
    }

    if (!formData?.banking_details?.account_name?.trim()) {
      newErrors['banking_details.account_name'] = 'Account name is required';
    }

    if (!formData?.banking_details?.bank_name?.trim()) {
      newErrors['banking_details.bank_name'] = 'Bank name is required';
    }

    if (!formData?.banking_details?.ifsc_code?.trim()) {
      newErrors['banking_details.ifsc_code'] = 'IFSC code is required';
    }

    // Validate Tax Details (required fields)
    if (!formData?.tax_details?.pan?.trim()) {
      newErrors['tax_details.pan'] = 'PAN is required';
    }

    if (!formData?.tax_details?.currency?.trim()) {
      newErrors['tax_details.currency'] = 'Currency is required';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return { isValid, errors: newErrors };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const validationResult = validateForm();
    if (!validationResult.isValid) {
      // Find the first section with errors and navigate to it
      const errorKeys = Object.keys(validationResult.errors);
      if (errorKeys.length > 0) {
        const firstErrorSection = errorKeys[0].split('.')[0];
        const sectionMap = {
          'basic_information': 'basic',
          'billing_address': 'billing',
          'shipping_address': 'shipping',
          'banking_details': 'banking',
          'tax_details': 'tax'
        };
        setActiveSection(sectionMap[firstErrorSection] || 'basic');
        setErrorMessage('Please fill in all required fields in all sections before submitting.');
      }
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const axiosConfig = {
        headers,
        timeout: REQUEST_TIMEOUT_MS
      };

      // Prepare payload according to API structure
      const payload = {
        customer_data: formData
      };

      if (mode === 'add') {
        // Create new customer
        const response = await axios.post(
          API_ENDPOINTS.CUSTOMERS_CREATE,
          payload,
          axiosConfig
        );
        
        // Get success message from API response or use default
        const apiSuccessMessage = response?.data?.message || 
                                 response?.data?.detail || 
                                 'Customer created successfully!';
        setSuccessMessage(apiSuccessMessage);
        
        setTimeout(() => {
          setSuccessMessage('');
          onSave(response.data);
          onClose();
        }, 2500);
      } else {
        // Update existing customer
        const response = await axios.put(
          API_ENDPOINTS.CUSTOMERS_UPDATE(customer?.id),
          payload,
          axiosConfig
        );
        
        // Get success message from API response or use default
        const apiSuccessMessage = response?.data?.message || 
                                 response?.data?.detail || 
                                 'Customer updated successfully!';
        setSuccessMessage(apiSuccessMessage);
        
        setTimeout(() => {
          setSuccessMessage('');
          onSave(response.data);
          onClose();
        }, 2500);
      }
    } catch (error) {
      const timeoutTriggered = error?.code === 'ECONNABORTED';
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.response?.data?.error ||
                      (timeoutTriggered ? 'Request timed out. Please check your network or try again in a moment.' : error?.message) || 
                      'Failed to save customer. Please try again.';
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyBillingToShipping = () => {
    setFormData(prev => ({
      ...prev,
      shipping_address: { ...prev.billing_address }
    }));
  };

  if (!isOpen) return null;

  // Check which sections have errors
  const getSectionErrors = (sectionPrefix) => {
    return Object.keys(errors).filter(key => key.startsWith(sectionPrefix)).length;
  };

  const sections = [
    { id: 'basic', label: 'Basic Information', prefix: 'basic_information' },
    { id: 'billing', label: 'Billing Address', prefix: 'billing_address' },
    { id: 'shipping', label: 'Shipping Address', prefix: 'shipping_address' },
    { id: 'banking', label: 'Banking Details', prefix: 'banking_details' },
    { id: 'tax', label: 'Tax Details', prefix: 'tax_details' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X size={20} />
          </Button>
        </div>

        {/* Section Tabs */}
        <div className="border-b border-gray-200 px-6 flex-shrink-0">
          <nav className="flex -mb-px space-x-4 overflow-x-auto">
            {sections.map((section) => {
              const hasErrors = getSectionErrors(section.prefix) > 0;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap relative ${
                    activeSection === section.id
                      ? 'border-blue-600 text-blue-600'
                      : hasErrors
                      ? 'border-transparent text-red-500 hover:text-red-700 hover:border-red-300'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {section.label}
                  {hasErrors && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Modal Content Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Success/Error Messages */}
            {successMessage && (
              <div className="p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
              </div>
            )}
            {errorMessage && (
              <div className="p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Basic Information Section */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Title"
                    type="text"
                    placeholder="Mr./Ms./Mrs."
                    value={formData?.basic_information?.title}
                    onChange={(e) => handleInputChange('basic_information', 'title', e?.target?.value)}
                  />
                  
                  <Input
                    label="Customer Name"
                    type="text"
                    placeholder="Enter customer name"
                    value={formData?.basic_information?.customer_name}
                    onChange={(e) => handleInputChange('basic_information', 'customer_name', e?.target?.value)}
                    error={errors?.['basic_information.customer_name']}
                    required
                  />
                  
                  <Input
                    label="Entity Type"
                    type="text"
                    placeholder="e.g., Private Limited Company"
                    value={formData?.basic_information?.entity_type}
                    onChange={(e) => handleInputChange('basic_information', 'entity_type', e?.target?.value)}
                    error={errors?.['basic_information.entity_type']}
                    required
                  />
                  
                  <Input
                    label="Customer ID"
                    type="text"
                    placeholder="e.g., CUST001"
                    value={formData?.basic_information?.customer_id}
                    onChange={(e) => handleInputChange('basic_information', 'customer_id', e?.target?.value)}
                    error={errors?.['basic_information.customer_id']}
                    required
                  />
                  
                  <Input
                    label="Mobile Number"
                    type="tel"
                    placeholder="+91-9876543210"
                    value={formData?.basic_information?.mobile_number}
                    onChange={(e) => handleInputChange('basic_information', 'mobile_number', e?.target?.value)}
                    error={errors?.['basic_information.mobile_number']}
                    required
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="contact@example.com"
                    value={formData?.basic_information?.email_address}
                    onChange={(e) => handleInputChange('basic_information', 'email_address', e?.target?.value)}
                    error={errors?.['basic_information.email_address']}
                    required
                  />
                  
                  <Input
                    label="Customer GSTIN"
                    type="text"
                    placeholder="07AABCU9603R1ZX"
                    value={formData?.basic_information?.customer_gstin}
                    onChange={(e) => handleInputChange('basic_information', 'customer_gstin', e?.target?.value)}
                  />
                  
                  <Input
                    label="GST Registered Name"
                    type="text"
                    placeholder="Company name as per GST"
                    value={formData?.basic_information?.gst_registered_name}
                    onChange={(e) => handleInputChange('basic_information', 'gst_registered_name', e?.target?.value)}
                  />
                  
                  <Input
                    label="GST Filing Status"
                    type="text"
                    placeholder="Regular/Composite"
                    value={formData?.basic_information?.gst_filing_status}
                    onChange={(e) => handleInputChange('basic_information', 'gst_filing_status', e?.target?.value)}
                  />
                  
                  <Input
                    label="Business Name"
                    type="text"
                    placeholder="Business trading name"
                    value={formData?.basic_information?.business_name}
                    onChange={(e) => handleInputChange('basic_information', 'business_name', e?.target?.value)}
                  />
                  
                  <Input
                    label="Display Name"
                    type="text"
                    placeholder="Display name"
                    value={formData?.basic_information?.display_name}
                    onChange={(e) => handleInputChange('basic_information', 'display_name', e?.target?.value)}
                  />
                  
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+91-11-23456789"
                    value={formData?.basic_information?.phone_number}
                    onChange={(e) => handleInputChange('basic_information', 'phone_number', e?.target?.value)}
                  />
                  
                  <Input
                    label="Fax"
                    type="text"
                    placeholder="Fax number"
                    value={formData?.basic_information?.fax}
                    onChange={(e) => handleInputChange('basic_information', 'fax', e?.target?.value)}
                  />
                </div>
              </div>
            )}

            {/* Billing Address Section */}
            {activeSection === 'billing' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Billing Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Address Line 1"
                    type="text"
                    placeholder="Street address"
                    value={formData?.billing_address?.address_line_1}
                    onChange={(e) => handleInputChange('billing_address', 'address_line_1', e?.target?.value)}
                    error={errors?.['billing_address.address_line_1']}
                    required
                  />
                  
                  <Input
                    label="Address Line 2"
                    type="text"
                    placeholder="Apartment, suite, etc."
                    value={formData?.billing_address?.address_line_2}
                    onChange={(e) => handleInputChange('billing_address', 'address_line_2', e?.target?.value)}
                  />
                  
                  <Input
                    label="City"
                    type="text"
                    placeholder="City"
                    value={formData?.billing_address?.city}
                    onChange={(e) => handleInputChange('billing_address', 'city', e?.target?.value)}
                    error={errors?.['billing_address.city']}
                    required
                  />
                  
                  <Input
                    label="State"
                    type="text"
                    placeholder="State"
                    value={formData?.billing_address?.state}
                    onChange={(e) => handleInputChange('billing_address', 'state', e?.target?.value)}
                    error={errors?.['billing_address.state']}
                    required
                  />
                  
                  <Input
                    label="Postal Code"
                    type="text"
                    placeholder="PIN code"
                    value={formData?.billing_address?.postal_code}
                    onChange={(e) => handleInputChange('billing_address', 'postal_code', e?.target?.value)}
                    error={errors?.['billing_address.postal_code']}
                    required
                  />
                  
                  <Input
                    label="Country"
                    type="text"
                    placeholder="Country"
                    value={formData?.billing_address?.country}
                    onChange={(e) => handleInputChange('billing_address', 'country', e?.target?.value)}
                    error={errors?.['billing_address.country']}
                    required
                  />
                  
                  <Input
                    label="Branch Name"
                    type="text"
                    placeholder="Branch name"
                    value={formData?.billing_address?.branch_name}
                    onChange={(e) => handleInputChange('billing_address', 'branch_name', e?.target?.value)}
                  />
                </div>
              </div>
            )}

            {/* Shipping Address Section */}
            {activeSection === 'shipping' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Shipping Address</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyBillingToShipping}
                  >
                    Copy from Billing
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Address Line 1"
                    type="text"
                    placeholder="Street address"
                    value={formData?.shipping_address?.address_line_1}
                    onChange={(e) => handleInputChange('shipping_address', 'address_line_1', e?.target?.value)}
                    error={errors?.['shipping_address.address_line_1']}
                    required
                  />
                  
                  <Input
                    label="Address Line 2"
                    type="text"
                    placeholder="Apartment, suite, etc."
                    value={formData?.shipping_address?.address_line_2}
                    onChange={(e) => handleInputChange('shipping_address', 'address_line_2', e?.target?.value)}
                  />
                  
                  <Input
                    label="City"
                    type="text"
                    placeholder="City"
                    value={formData?.shipping_address?.city}
                    onChange={(e) => handleInputChange('shipping_address', 'city', e?.target?.value)}
                    error={errors?.['shipping_address.city']}
                    required
                  />
                  
                  <Input
                    label="State"
                    type="text"
                    placeholder="State"
                    value={formData?.shipping_address?.state}
                    onChange={(e) => handleInputChange('shipping_address', 'state', e?.target?.value)}
                    error={errors?.['shipping_address.state']}
                    required
                  />
                  
                  <Input
                    label="Postal Code"
                    type="text"
                    placeholder="PIN code"
                    value={formData?.shipping_address?.postal_code}
                    onChange={(e) => handleInputChange('shipping_address', 'postal_code', e?.target?.value)}
                    error={errors?.['shipping_address.postal_code']}
                    required
                  />
                  
                  <Input
                    label="Country"
                    type="text"
                    placeholder="Country"
                    value={formData?.shipping_address?.country}
                    onChange={(e) => handleInputChange('shipping_address', 'country', e?.target?.value)}
                    error={errors?.['shipping_address.country']}
                    required
                  />
                  
                  <Input
                    label="Branch Name"
                    type="text"
                    placeholder="Branch name"
                    value={formData?.shipping_address?.branch_name}
                    onChange={(e) => handleInputChange('shipping_address', 'branch_name', e?.target?.value)}
                  />
                </div>
              </div>
            )}

            {/* Banking Details Section */}
            {activeSection === 'banking' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Banking Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Account Number"
                    type="text"
                    placeholder="Bank account number"
                    value={formData?.banking_details?.account_number}
                    onChange={(e) => handleInputChange('banking_details', 'account_number', e?.target?.value)}
                    error={errors?.['banking_details.account_number']}
                    required
                  />
                  
                  <Input
                    label="Account Name"
                    type="text"
                    placeholder="Account holder name"
                    value={formData?.banking_details?.account_name}
                    onChange={(e) => handleInputChange('banking_details', 'account_name', e?.target?.value)}
                    error={errors?.['banking_details.account_name']}
                    required
                  />
                  
                  <Input
                    label="Bank Name"
                    type="text"
                    placeholder="Bank name"
                    value={formData?.banking_details?.bank_name}
                    onChange={(e) => handleInputChange('banking_details', 'bank_name', e?.target?.value)}
                    error={errors?.['banking_details.bank_name']}
                    required
                  />
                  
                  <Input
                    label="IFSC Code"
                    type="text"
                    placeholder="IFSC code"
                    value={formData?.banking_details?.ifsc_code}
                    onChange={(e) => handleInputChange('banking_details', 'ifsc_code', e?.target?.value)}
                    error={errors?.['banking_details.ifsc_code']}
                    required
                  />
                  
                  <Input
                    label="Account Type"
                    type="text"
                    placeholder="Savings/Current"
                    value={formData?.banking_details?.account_type}
                    onChange={(e) => handleInputChange('banking_details', 'account_type', e?.target?.value)}
                  />
                  
                  <Input
                    label="Branch Name"
                    type="text"
                    placeholder="Bank branch name"
                    value={formData?.banking_details?.branch_name}
                    onChange={(e) => handleInputChange('banking_details', 'branch_name', e?.target?.value)}
                  />
                </div>
              </div>
            )}

            {/* Tax Details Section */}
            {activeSection === 'tax' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Tax Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="PAN"
                    type="text"
                    placeholder="PAN number"
                    value={formData?.tax_details?.pan}
                    onChange={(e) => handleInputChange('tax_details', 'pan', e?.target?.value)}
                    error={errors?.['tax_details.pan']}
                    required
                  />
                  
                  <Input
                    label="TAN"
                    type="text"
                    placeholder="TAN number"
                    value={formData?.tax_details?.tan}
                    onChange={(e) => handleInputChange('tax_details', 'tan', e?.target?.value)}
                  />
                  
                  <Input
                    label="COA"
                    type="text"
                    placeholder="Chart of Accounts"
                    value={formData?.tax_details?.coa}
                    onChange={(e) => handleInputChange('tax_details', 'coa', e?.target?.value)}
                  />
                  
                  <Input
                    label="TDS Slab Rate"
                    type="text"
                    placeholder="e.g., 10%"
                    value={formData?.tax_details?.tds_slab_rate}
                    onChange={(e) => handleInputChange('tax_details', 'tds_slab_rate', e?.target?.value)}
                  />
                  
                  <Input
                    label="Currency"
                    type="text"
                    placeholder="INR/USD"
                    value={formData?.tax_details?.currency}
                    onChange={(e) => handleInputChange('tax_details', 'currency', e?.target?.value)}
                    error={errors?.['tax_details.currency']}
                    required
                  />
                  
                  <Input
                    label="MSME Number"
                    type="text"
                    placeholder="MSME registration number"
                    value={formData?.tax_details?.msme_number}
                    onChange={(e) => handleInputChange('tax_details', 'msme_number', e?.target?.value)}
                  />
                  
                  <Input
                    label="Customer Code"
                    type="text"
                    placeholder="Internal customer code"
                    value={formData?.tax_details?.customer_code}
                    onChange={(e) => handleInputChange('tax_details', 'customer_code', e?.target?.value)}
                  />
                  
                  <div className="col-span-2 space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData?.tax_details?.apply_reverse_charge_by_default}
                        onChange={(e) => handleInputChange('tax_details', 'apply_reverse_charge_by_default', e?.target?.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Apply Reverse Charge by Default</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData?.tax_details?.export_or_sez_developer}
                        onChange={(e) => handleInputChange('tax_details', 'export_or_sez_developer', e?.target?.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Export or SEZ Developer</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting || !!successMessage}
            >
              {isSubmitting ? 'Saving...' : (successMessage ? 'Success!' : (mode === 'edit' ? 'Update Customer' : 'Add Customer'))}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;

